/**
 * Rating scopes - predefined sets of countries users can choose to rate.
 *
 * Scopes let users focus on a manageable subset of countries
 * and produce shareable results specific to that scope.
 */

import type { CountryCode, Country } from './types';
import { europeCountries } from './europe';
import { asiaCountries } from './asia';
import { northAmericaCountries, southAmericaCountries } from './americas';
import { oceaniaCountries } from './oceania';
import { africaCountries } from './africa';

/**
 * All countries - constructed here to avoid circular dependency with index.ts
 */
const countries: Country[] = [
  ...europeCountries,
  ...asiaCountries,
  ...northAmericaCountries,
  ...southAmericaCountries,
  ...oceaniaCountries,
  ...africaCountries,
];

/**
 * A rating scope definition.
 */
export interface RatingScope {
  /** Unique identifier */
  id: string;

  /** Display name */
  name: string;

  /** Short description */
  description: string;

  /** Icon/emoji */
  icon: string;

  /** Country codes included in this scope */
  countryCodes: CountryCode[];

  /** Optional accent color class */
  accentColor?: string;
}

/**
 * Top 100 most visited/significant countries.
 * Based on tourism data, economic significance, and global relevance.
 */
const TOP_100_CODES: CountryCode[] = [
  // Europe (30)
  'GB', 'FR', 'DE', 'IT', 'ES', 'NL', 'PT', 'GR', 'CH', 'AT',
  'BE', 'SE', 'NO', 'DK', 'FI', 'IE', 'PL', 'CZ', 'HU', 'HR',
  'RO', 'BG', 'SI', 'SK', 'EE', 'LV', 'LT', 'LU', 'MT', 'CY',
  // Asia (25)
  'JP', 'CN', 'IN', 'KR', 'TH', 'VN', 'MY', 'SG', 'ID', 'PH',
  'TW', 'HK', 'AE', 'IL', 'TR', 'SA', 'QA', 'KW', 'OM', 'JO',
  'LB', 'KH', 'LK', 'NP', 'BD',
  // Americas (20)
  'US', 'CA', 'MX', 'BR', 'AR', 'CL', 'CO', 'PE', 'EC', 'CR',
  'PA', 'CU', 'DO', 'JM', 'TT', 'BS', 'PR', 'UY', 'PY', 'BO',
  // Africa (15)
  'ZA', 'EG', 'MA', 'TN', 'KE', 'TZ', 'NG', 'GH', 'ET', 'RW',
  'UG', 'MU', 'SC', 'BW', 'NA',
  // Oceania (10)
  'AU', 'NZ', 'FJ', 'PG', 'WS', 'TO', 'VU', 'SB', 'NC', 'PF',
];

/**
 * Get country codes by continent from our data.
 */
function getCountryCodesByContinent(continent: string): CountryCode[] {
  return countries
    .filter(c => c.continent === continent)
    .map(c => c.code);
}

/**
 * All available rating scopes.
 */
export const ratingScopes: RatingScope[] = [
  {
    id: 'world-all',
    name: 'World',
    description: 'Rate every country on Earth',
    icon: '\u{1F30D}',
    countryCodes: countries.map(c => c.code),
    accentColor: 'bg-blue-500',
  },
  {
    id: 'top-100',
    name: 'Top 100',
    description: 'The most visited countries',
    icon: '\u{1F525}',
    countryCodes: TOP_100_CODES.filter(code =>
      countries.some(c => c.code === code)
    ),
    accentColor: 'bg-orange-500',
  },
  {
    id: 'europe',
    name: 'Europe',
    description: 'European nations',
    icon: '\u{1F1EA}\u{1F1FA}',
    countryCodes: getCountryCodesByContinent('Europe'),
    accentColor: 'bg-indigo-500',
  },
  {
    id: 'asia',
    name: 'Asia',
    description: 'Asian nations',
    icon: '\u{1F3EF}',
    countryCodes: getCountryCodesByContinent('Asia'),
    accentColor: 'bg-rose-500',
  },
  {
    id: 'americas',
    name: 'Americas',
    description: 'North & South America',
    icon: '\u{1F5FD}',
    countryCodes: [
      ...getCountryCodesByContinent('North America'),
      ...getCountryCodesByContinent('South America'),
    ],
    accentColor: 'bg-emerald-500',
  },
  {
    id: 'africa',
    name: 'Africa',
    description: 'African nations',
    icon: '\u{1F98D}',
    countryCodes: getCountryCodesByContinent('Africa'),
    accentColor: 'bg-amber-500',
  },
  {
    id: 'oceania',
    name: 'Oceania',
    description: 'Pacific region',
    icon: '\u{1F3DD}\uFE0F',
    countryCodes: getCountryCodesByContinent('Oceania'),
    accentColor: 'bg-cyan-500',
  },
];

/**
 * Get a scope by ID.
 */
export function getScopeById(id: string): RatingScope | undefined {
  return ratingScopes.find(s => s.id === id);
}

/**
 * Get the default scope.
 */
export function getDefaultScope(): RatingScope {
  return ratingScopes.find(s => s.id === 'europe') ?? ratingScopes[0];
}

/**
 * Get countries for a scope.
 */
export function getCountriesForScope(scopeId: string) {
  const scope = getScopeById(scopeId);
  if (!scope) return [];

  return countries.filter(c => scope.countryCodes.includes(c.code));
}

/**
 * Calculate progress for a scope given ratings.
 */
export function getScopeProgress(scopeId: string, ratings: Record<string, number>) {
  const scope = getScopeById(scopeId);
  if (!scope) return { rated: 0, total: 0, percentage: 0 };

  const rated = scope.countryCodes.filter(code => ratings[code] !== undefined).length;
  const total = scope.countryCodes.length;
  const percentage = total > 0 ? Math.round((rated / total) * 100) : 0;

  return { rated, total, percentage };
}
